import React, { useState, useEffect } from "react";

const formatCurrency = (v, c = "INR") =>
  ({ INR: "₹", USD: "$", EUR: "€" }[c] || "") + Number(v || 0).toLocaleString();

// deterministic color generator per stage name/index
const colorForStageIndex = (i) => `rgba(${100 + ((i * 40) % 155)}, ${50 + ((i * 60) % 155)}, ${150 + ((i * 30) % 105)}, 0.85)`;

export default function MemberStageRing({ member, onClick, size = 320, onStageClick, selectedStageId, allStageNames = null, stageColorMap = null, partitionGap = 12 }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [segments, setSegments] = useState([]);
  const [stats, setStats] = useState({ totalDeals: 0, totalRevenue: 0, avgRiskScore: null });

  const strokeWidth = 18;
  const radius = (size - strokeWidth) / 2;

  // useEffect(() => {
  //   // For a fresh start: partition equally by number of stages (ignore deal counts)
  //   const stagesArr = Array.isArray(member.stages) ? member.stages : [];
  //   const totalDeals = stagesArr.reduce((s, st) => s + (st.dealsCount || (Array.isArray(st.deals) ? st.deals.length : 0) || 0), 0);
  //   const totalRevenue = stagesArr.reduce((s, st) => s + (st.revenue || 0), 0);
  //   setStats({ totalDeals, totalRevenue });

  //   const stageOrder = (Array.isArray(allStageNames) && allStageNames.length) ? allStageNames : stagesArr.map(s => s.stageName || s.name || "");
  //   const n = Math.max(1, stageOrder.length);
  //   let currentAngle = 0;
  //   const segs = stageOrder.map((stageName, idx) => {
  //     const stage = stagesArr.find(s => (s.stageName || s.name) === stageName) || {};
  //     const dealsCount = stage.dealsCount !== undefined ? stage.dealsCount : (Array.isArray(stage.deals) ? stage.deals.length : 0);
  //     const arcAngle = 360 / n;
  //     const startAngle = currentAngle + partitionGap / 2;
  //     const endAngle = currentAngle + arcAngle - partitionGap / 2;
  //     currentAngle += arcAngle;
  //     const color = (stageColorMap && stageColorMap[stageName]) ? stageColorMap[stageName] : colorForStageIndex(idx);
  //     const midAngle = (startAngle + endAngle) / 2;
  //     const percent = totalDeals ? Math.round((dealsCount / totalDeals) * 100) : 0;
  //     return {
  //       stageId: stage.stageId || stage._id || null,
  //       stageName,
  //       dealsCount,
  //       revenue: stage.revenue || 0,
  //       startAngle,
  //       endAngle,
  //       midAngle,
  //       color,
  //       percent,
  //       stage, // keep original stage object to show deal names on hover
  //     };
  //   });
  //   setSegments(segs);
  // }, [member, allStageNames, stageColorMap, partitionGap]);



  useEffect(() => {
  let stagesArr = [];

  // 🔥 FIX IS HERE
  if (Array.isArray(member?.stages)) {
    stagesArr = member.stages;
  } else if (member?.stages && typeof member.stages === "object") {
    stagesArr = Object.entries(member.stages).map(
      ([stageName, dealsCount]) => ({
        stageName,
        dealsCount,
      })
    );
  }

  // If backend returned detailed items per stage (stageDetails), prefer that for accurate counts
  // stageDetails shape: { stageName: [ { _id, title, createdAt, ... }, ... ], ... }
  if (member?.stageDetails && typeof member.stageDetails === 'object') {
    stagesArr = Object.entries(member.stageDetails).map(([stageName, items]) => ({
      stageName,
      dealsCount: Array.isArray(items) ? items.length : 0,
      deals: Array.isArray(items) ? items : [],
    }));
    // debug: log when using stageDetails so we can trace counts
    try {
      // eslint-disable-next-line no-console
      console.debug('[MemberStageRing] using stageDetails for member', member?.id || member?.name || member?.memberId, 'stages:', stagesArr.map(s=>({ name: s.stageName, count: s.dealsCount })));
    } catch (e) {}
  }


  const totalDeals = stagesArr.reduce(
    (s, st) => s + (st.dealsCount || 0),
    0
  );

  // Compute total revenue: prefer summing item.value or item.revenue from deals array when available
  const totalRevenue = stagesArr.reduce((sum, st) => {
    if (Array.isArray(st.deals) && st.deals.length) {
      const stageSum = st.deals.reduce((ss, d) => {
        const v = d && (d.value || d.revenue || d.amount || 0);
        return ss + (isNaN(Number(v)) ? 0 : Number(v));
      }, 0);
      return sum + stageSum;
    }
    // fallback to st.revenue if present
    const rv = st.revenue || 0;
    return sum + (isNaN(Number(rv)) ? 0 : Number(rv));
  }, 0);

  // Calculate average risk score from all deals in all stages
  let allRiskScores = [];
  stagesArr.forEach(st => {
    if (Array.isArray(st.deals)) {
      st.deals.forEach(d => {
        if (d && typeof d.riskScore === "number" && !isNaN(d.riskScore)) {
          allRiskScores.push(d.riskScore);
        }
      });
    }
  });
  const avgRiskScore = allRiskScores.length
    ? allRiskScores.reduce((a, b) => a + b, 0) / allRiskScores.length
    : null;

  setStats({ totalDeals, totalRevenue, avgRiskScore });

  const stageOrder =
    (Array.isArray(allStageNames) && allStageNames.length)
      ? allStageNames
      : stagesArr.map(s => s.stageName);

  let currentAngle = 0;
    const n = Math.max(1, stageOrder.length);

    const segs = stageOrder.map((stageName, idx) => {
      const stage = stagesArr.find(s => s.stageName === stageName) || {};
      const arcAngle = 360 / n;
      
      // Smart gap: if segments are very thin, reduce gap automatically
      const safeGap = Math.min(partitionGap, arcAngle * 0.5);

      const startAngle = currentAngle + safeGap / 2;
      const endAngle = currentAngle + arcAngle - safeGap / 2;
      currentAngle += arcAngle;

    // compute revenue for this stage (prefer deals array values)
    let stageRevenue = 0;
    if (Array.isArray(stage.deals) && stage.deals.length) {
      stageRevenue = stage.deals.reduce((ss, d) => {
        const v = d && (d.value || d.revenue || d.amount || 0);
        return ss + (isNaN(Number(v)) ? 0 : Number(v));
      }, 0);
    } else {
      stageRevenue = stage.revenue || 0;
    }

    return {
      stageName,
      dealsCount: stage.dealsCount || 0,
      revenue: stageRevenue,
      startAngle,
      endAngle,
      midAngle: (startAngle + endAngle) / 2,
      color: stageColorMap?.[stageName] || colorForStageIndex(idx),
      percent: totalDeals
        ? Math.round((stage.dealsCount / totalDeals) * 100)
        : 0,
    };
  });

  setSegments(segs);
  // debug: final stats
  try {
    // eslint-disable-next-line no-console
    console.debug('[MemberStageRing] member', member?.id || member?.name, 'totalDeals', totalDeals, 'totalRevenue', totalRevenue);
  } catch (e) {}
}, [member, allStageNames, stageColorMap, partitionGap]);

  const polarToCartesian = (cx, cy, r, angle) => {
    const rad = (angle - 90) * (Math.PI / 180.0);
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad), rad };
  };
  const describeArc = (x, y, r, startAngle, endAngle) => {
    const start = polarToCartesian(x, y, r, endAngle);
    const end = polarToCartesian(x, y, r, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    return ` M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
  };

  const innerR = radius - strokeWidth / 2;
  const outerR = radius + strokeWidth / 2;

  const riskBadgeColor =
    member.riskScore >= 70 ? "#ff4d4f" : member.riskScore >= 40 ? "#faad14" : "#52c41a";

  return (
    <div style={{ position: "relative", width: size, height: size, overflow: 'visible' }}>
      <svg 
        width={size} 
        height={size} 
        style={{ cursor: onClick ? "pointer" : "default", overflow: 'visible' }} 
        onClick={onClick}
      >
        <g transform={`translate(${size / 2}, ${size / 2})`}>
          <circle cx={0} cy={0} r={radius} stroke="#f2f2f2" strokeWidth={strokeWidth} fill="none" />

          {/* Draw arcs with enhanced hit area */}
          {segments.map((seg, i) => {
            const isSelected = selectedStageId === seg.stageId;
            const isHovered = hoveredIdx === i;
            return (
              <g
                key={seg.stageId || i}
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
                onClick={(e) => {
                  e.stopPropagation();
                  onStageClick && onStageClick(seg);
                }}
                style={{ cursor: onStageClick ? "pointer" : "default" }}
              >
                {/* Invisible hit area (wider stroke) to make hovering much easier */}
                <path
                  d={describeArc(0, 0, radius, seg.startAngle, seg.endAngle)}
                  stroke="transparent"
                  strokeWidth={strokeWidth + 20}
                  fill="none"
                />
                {/* Visible segment path */}
                <path
                  d={describeArc(0, 0, radius, seg.startAngle, seg.endAngle)}
                  stroke={seg.color}
                  strokeWidth={isSelected ? strokeWidth + 3 : isHovered ? strokeWidth + 2 : strokeWidth}
                  opacity={isSelected || isHovered ? 1 : 0.85}
                  fill="none"
                  strokeLinecap="butt"
                  style={{ 
                    transition: "stroke-width 0.2s ease, opacity 0.2s ease",
                    pointerEvents: "none" // Let the transparent hit area handle interactions
                  }}
                />
              </g>
            );
          })}

          {/* Separators */}
          {segments.map((seg, i) => {
            const p1 = polarToCartesian(0, 0, innerR, seg.startAngle);
            const p2 = polarToCartesian(0, 0, outerR, seg.startAngle);
            return (
              <line
                key={`sep-${i}`}
                x1={p1.x}
                y1={p1.y}
                x2={p2.x}
                y2={p2.y}
                stroke="#ffffff"
                strokeWidth={2}
                strokeLinecap="round"
                opacity={0.9}
              />
            );
          })}

          {/* Hover info placeholder removed from SVG to prevent overlap */}
        </g>
      </svg>

      {/* Center Info */}
      <div
        title={member.name}
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: size - 40,
          height: size - 40,
          transform: "translate(-50%, -50%)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          pointerEvents: "none",
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: "#222" }}>{member.name}</div>
        <div style={{ fontSize: 14, color: "#555" }}>Total Deals</div>
        <div style={{ fontSize: 24, fontWeight: 800, color: "#111", marginBottom: 8 }}>
          {stats.totalDeals}
        </div>
        {stats.avgRiskScore !== null && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "4px 10px",
              borderRadius: 999,
              background: "#f7f7f7",
              border: "1px solid #eee",
              marginBottom: 8,
            }}
          >
            <span style={{ fontSize: 13, color: "#666" }}>Avg. Risk Score</span>
            <strong style={{ color: riskBadgeColor }}>{stats.avgRiskScore.toFixed(1)}</strong>
          </div>
        )}
        <div style={{ fontSize: 12, color: "#777" }}>Total Revenue</div>
        <div style={{ fontSize: 13, fontWeight: 700, maxWidth: size - 60, wordBreak: "break-word", lineHeight: 1.3, textAlign: "center" }}>
          {(() => {
            const rev = stats.totalRevenue;
            const sym = ({ INR: "₹", USD: "$", EUR: "€" }[member.currency] || "");
            if (rev >= 1e7) return `${sym}${(rev / 1e7).toFixed(1)}Cr`;
            if (rev >= 1e5) return `${sym}${(rev / 1e5).toFixed(1)}L`;
            if (rev >= 1e3) return `${sym}${(rev / 1e3).toFixed(1)}K`;
            return `${sym}${Number(rev || 0).toLocaleString()}`;
          })()}
        </div>
        </div>
      
      {/* Opaque HTML Tooltip - Smart edge-aware positioning to avoid clipping */}
      {hoveredIdx !== null && segments[hoveredIdx] && (() => {
        const seg = segments[hoveredIdx];
        const pMid = polarToCartesian(size / 2, size / 2, radius, seg.midAngle);
        
        // Flip tooltip vertically if it's in the top 25% of the ring to avoid clipping
        const shouldFlip = pMid.y < size * 0.25;
        const yOffset = shouldFlip ? 24 : -12;
        const translateY = shouldFlip ? "0" : "-100%";

        // Handle horizontal clipping at edges
        // If pMid.x is close to 0 (left edge) or close to size (right edge), 
        // we shift the tooltip so it doesn't go off-screen.
        const edgeThreshold = 70;
        const isLeftEdge = pMid.x < edgeThreshold;
        const isRightEdge = pMid.x > size - edgeThreshold;
        
        const translateX = isLeftEdge ? "0%" : (isRightEdge ? "-100%" : "-50%");
        const leftPos = isLeftEdge ? Math.max(10, pMid.x - 20) : (isRightEdge ? Math.min(size - 10, pMid.x + 20) : pMid.x);

        return (
          <div
            style={{
              position: "absolute",
              left: leftPos,
              top: pMid.y + yOffset,
              transform: `translate(${translateX}, ${translateY})`,
              background: "#ffffff",
              color: "#1e293b",
              padding: "8px 16px",
              borderRadius: "8px",
              fontSize: "12px",
              fontWeight: "700",
              whiteSpace: "nowrap",
              boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(0,0,0,0.05)",
              pointerEvents: "none",
              zIndex: 9999,
              border: `1px solid ${seg.color}`,
              animation: "ringTooltipFadeIn 0.2s ease-out forwards",
            }}
          >
            <style>{`
              @keyframes ringTooltipFadeIn {
                from { opacity: 0; transform: translate(${translateX}, ${shouldFlip ? "-5%" : "-95%"}); }
                to { opacity: 1; transform: translate(${translateX}, ${translateY}); }
              }
            `}</style>
            <span style={{ color: seg.color, marginRight: 6 }}>●</span>
            {seg.stageName} <span style={{ opacity: 0.6, marginLeft: 4 }}>{seg.percent}%</span>
          </div>
        );
      })()}
    </div>
  );
}